import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import ClaireoaksLogo from '../components/ClaireoaksLogo';
import { validateOTP } from '../utils/otpValidation';

const { useState } = React;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [tempUserCredential, setTempUserCredential] = useState(null);
  const [resetSending, setResetSending] = useState(false);

  async function handleInitialSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      setInfo('');
      setLoading(true);
      
      // First login the user
      const userCredential = await login(email, password);
      setTempUserCredential(userCredential);
      setShowOtpStep(true);
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      setInfo('');
      setLoading(true);

      if (!validateOTP(otp)) {
        throw new Error('Invalid OTP');
      }

      // Get user role from Firestore
      const userDoc = await getDoc(doc(db, 'users', tempUserCredential.user.uid));
      const userData = userDoc.data();
      
      // Redirect based on role
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      if (error.message === 'Invalid OTP') {
        setError('Invalid OTP. Please try again.');
      } else {
        setError('Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    if (resetSending) return;
    try {
      setError('');
      setInfo('');
      const targetEmail = String(email || '').trim();
      if (!targetEmail) {
        setError('Enter your email first, then click “Forgot your password?”.');
        return;
      }
      setResetSending(true);
      await sendPasswordResetEmail(auth, targetEmail);
      setInfo('Password reset email sent. Check your inbox (and spam/junk).');
    } catch (err) {
      console.error('Password reset error:', err);
      // Avoid leaking whether an email exists; still give a helpful message.
      setInfo('If that email is registered, a password reset link has been sent.');
    } finally {
      setResetSending(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Left side - Visual */}
      <div className="relative hidden overflow-hidden bg-oak-900 text-white md:block md:w-1/2">
        <img src="/images/community.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" aria-hidden />
        <div className="absolute inset-0 bg-oak-canopy" />
        <div className="relative flex h-full flex-col justify-end p-10 lg:p-14">
          <p className="font-display text-4xl font-semibold">Claireoaks</p>
          <h2 className="mt-3 font-display text-2xl font-medium text-claire-100">Secure member sign-in</h2>
          <p className="mt-3 max-w-sm text-oak-200">Clear banking, rooted in community.</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="md:w-1/2 flex flex-col">
        <main className="flex-grow flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="w-full max-w-md">
            <div className="flex justify-center md:justify-start">
              <ClaireoaksLogo />
            </div>
            
            <h2 className="mt-6 font-display text-3xl font-semibold text-oak-900">
              {showOtpStep ? 'Verify OTP' : 'Welcome back'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {showOtpStep ? 'Enter the OTP to complete sign in' : 'Sign in to access your account'}
            </p>

            {error && (
              <div className="mt-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg animate-fadeIn" role="alert">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {info && (
              <div className="mt-6 bg-emerald-50 border-l-4 border-emerald-400 p-4 rounded-lg animate-fadeIn" role="status">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-emerald-800">{info}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              {!showOtpStep ? (
                <form className="space-y-6" onSubmit={handleInitialSubmit}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="appearance-none block w-full pl-10 pr-4 py-3 rounded-xl border 
                          border-gray-200 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 
                          transition-colors"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="appearance-none block w-full pl-10 pr-4 py-3 rounded-xl border 
                          border-gray-200 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 
                          transition-colors"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500/20 border-gray-300 rounded"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                        Remember me
                      </label>
                    </div>

                    <div className="text-sm">
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={resetSending || loading}
                        className="font-medium text-primary-600 hover:text-primary-700 transition-colors disabled:opacity-60"
                      >
                        Forgot your password?
                      </button>
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex justify-center py-3 px-4 border border-transparent 
                        rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-secondary-600 
                        hover:from-primary-700 hover:to-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                        focus:ring-primary-500 transition-all"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing in...
                        </>
                      ) : (
                        'Continue'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <form className="space-y-6" onSubmit={handleOtpSubmit}>
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                      One-Time Password (OTP)
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <input
                        id="otp"
                        name="otp"
                        type="text"
                        required
                        maxLength="6"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="appearance-none block w-full pl-10 pr-4 py-3 rounded-xl border 
                          border-gray-200 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 
                          transition-colors text-center tracking-[0.5em] font-mono"
                        placeholder="000000"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOtpStep(false);
                        setOtp('');
                        setError('');
                      }}
                      className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-700
                        hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 
                        focus:ring-primary-500 transition-all"
                      disabled={loading}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="flex-1 flex justify-center py-3 px-4 border border-transparent 
                        rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r 
                        from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 
                        transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Verifying...
                        </>
                      ) : (
                        'Verify & Sign in'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </main>
        
        <footer className="py-4 text-center">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} Claireoaks Credit Union. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}